/**
 * Constellation view (doc 147 §5.3, §9 Phase 2).
 *
 * The opt-in global graph. WebGL primary (Sigma, lazy-loaded); on machines
 * without WebGL it feature-detects and falls back to a grouped text view so the
 * model is still navigable (PERF-05). Filter chips prune the view without
 * re-running layout; clicking a node focuses its 1-hop neighbourhood.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import { buildGraph, type LayoutCoords } from '../graph/buildGraph.js';
import { buildConstellationSvg } from '../graph/exportGraph.js';
import type { Mode, GraphContrast } from '../theme/tokens.js';
import { detectWebGL } from '../graph/webglSupport.js';
import type { ConstellationController } from '../graph/render.js';
import { applyFilters, defaultFilters, type ConstellationFilters as Filters } from '../state/filters.js';
import { domainsSorted, entitiesInDomain, computeDomainStats, selectCatalogDomains } from '../state/selectors.js';
import { navigate, type Route } from '../state/hashRouter.js';
import { ConstellationFilters } from './ConstellationFilters.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { DomainBadge } from './DomainBadge.js';
import { LayerBadge } from './LayerBadge.js';
import { ConnectivityPill } from './ConnectivityPill.js';

export interface ConstellationViewProps {
  manifest: AtlasManifest;
  layout: LayoutCoords;
  /** Resolved palette mode for node colour baking + SVG export. */
  mode: Mode;
  /** Graph contrast level; changes re-trigger a controller refresh. */
  graphContrast: GraphContrast;
  /** Optional route with domainFilter to pre-populate the domain filter. */
  route?: Extract<Route, { kind: 'constellation' }>;
  /** Active entity id to highlight in the constellation; preserved across view changes. */
  activeEntityId?: string | null;
}

/** WebGL-free fallback — entities grouped by domain, honouring the filters. */
function TextFallback({
  manifest,
  visible,
}: {
  manifest: AtlasManifest;
  visible: Set<string>;
}): React.JSX.Element {
  const domains = domainsSorted(manifest).filter((d) =>
    entitiesInDomain(manifest, d.id).some((e) => visible.has(e.id)),
  );
  const statsMap = useMemo(
    () => new Map(computeDomainStats(manifest).map((s) => [s.domainId, s])),
    [manifest],
  );
  return (
    <div className="constellation-fallback">
      <div className="banner" role="status">
        <Icon name="visibility_off" size="sm" />
        <span>WebGL unavailable — showing the text view of the model.</span>
      </div>
      {domains.length === 0 ? (
        <EmptyState icon="filter_list" title="No entities match the current filters" />
      ) : (
        domains.map((domain) => {
          const stats = statsMap.get(domain.id);
          return (
            <section key={domain.id} style={{ marginTop: 'var(--sp-md)' }}>
              <div className="section-label" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <DomainBadge domainId={domain.id} size="sm" />
                <LayerBadge layer={(domain.layer as DomainLayer) ?? 'L5'} size="sm" />
                {stats && <ConnectivityPill status={stats.connectivity} />}
              </div>
              {entitiesInDomain(manifest, domain.id)
                .filter((e) => visible.has(e.id))
                .map((entity) => (
                  <button
                    key={entity.id}
                    className="ref-row"
                    onClick={() => navigate({ kind: 'entity', id: entity.id })}
                  >
                    <Icon name="scatter_plot" size="sm" />
                    <span className="mono">{entity.name}</span>
                  </button>
                ))}
            </section>
          );
        })
      )}
    </div>
  );
}

export function ConstellationView({
  manifest,
  layout,
  mode,
  graphContrast,
  route,
  activeEntityId,
}: ConstellationViewProps): React.JSX.Element {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [gapsOnly, setGapsOnly] = useState(false);
  const webglAvailable = useMemo(() => detectWebGL(), []);

  // Initialize domain filter from route on mount
  useEffect(() => {
    if (route?.domainFilter) {
      setFilters((prev) => ({ ...prev, domain: route.domainFilter! }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild on mode change so node colours come from the active palette.
  const graph = useMemo(
    () => buildGraph(manifest, layout, { mode }),
    [manifest, layout, mode],
  );
  const visible = useMemo(() => applyFilters(manifest, filters), [manifest, filters]);

  const catalogDomains = useMemo(() => selectCatalogDomains(manifest), [manifest]);
  const gapDomainIds = useMemo(
    () => new Set(catalogDomains.filter((d) => d.gapFlags.length > 0).map((d) => d.id)),
    [catalogDomains],
  );

  const visibleAfterGaps = useMemo(() => {
    if (!gapsOnly) return visible;
    return new Set(
      [...visible].filter((id) => {
        const entity = manifest.entities.find((e) => e.id === id);
        return entity ? gapDomainIds.has(entity.domain) : false;
      }),
    );
  }, [visible, gapsOnly, manifest.entities, gapDomainIds]);

  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ConstellationController | null>(null);

  /* c8 ignore start — Sigma mount/teardown; needs a real WebGL context. */
  useEffect(() => {
    if (!webglAvailable || !containerRef.current) return;
    let disposed = false;
    const container = containerRef.current;
    void import('../graph/render.js').then(({ renderConstellation }) => {
      if (disposed) return;
      controllerRef.current = renderConstellation(container, graph, (nodeId) => {
        if (nodeId) navigate({ kind: 'entity', id: nodeId });
      });
    });
    return () => {
      disposed = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [webglAvailable, graph]);

  useEffect(() => {
    controllerRef.current?.setVisible(
      visibleAfterGaps.size === manifest.entities.length ? null : visibleAfterGaps,
    );
  }, [visibleAfterGaps, manifest.entities.length]);

  // CSS-var swaps (mode/contrast) only show up in the canvas on the next
  // sigma.refresh(); kick one whenever graphContrast changes. (Mode changes
  // already re-run the graph-rebuild effect above, which mounts a new
  // controller, so they pick up the new vars at mount.)
  useEffect(() => {
    controllerRef.current?.refresh();
  }, [graphContrast]);

  // Highlight the active entity when it's set from another view
  useEffect(() => {
    if (activeEntityId) {
      controllerRef.current?.setFocus(activeEntityId);
    }
  }, [activeEntityId]);

  /** Export the current graph as a downloadable SVG (browser-only). */
  const exportSvg = (): void => {
    const svg = buildConstellationSvg(graph, { mode });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atlas-constellation.svg';
    a.click();
    URL.revokeObjectURL(url);
  };
  /* c8 ignore stop */

  return (
    <div className="constellation">
      <ConstellationFilters
        manifest={manifest}
        filters={filters}
        onChange={setFilters}
        visibleCount={visibleAfterGaps.size}
        showGapsOnly={gapsOnly}
        onGapsToggle={setGapsOnly}
      />
      <div className="filter-bar">
        <button className="btn btn-ghost" onClick={exportSvg} aria-label="Export constellation as SVG">
          <Icon name="file_download" size="sm" />
          Export SVG
        </button>
      </div>
      {webglAvailable ? (
        <div
          ref={containerRef}
          className="constellation-canvas"
          role="img"
          aria-label={`Constellation graph — ${visibleAfterGaps.size} entities`}
        />
      ) : (
        <TextFallback manifest={manifest} visible={visibleAfterGaps} />
      )}

      {/* Domain cluster legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          background: 'rgba(13,17,23,0.82)',
          border: '1px solid #30363d',
          borderRadius: 8,
          padding: '8px 10px',
          fontSize: 11,
          color: '#c9d1d9',
          maxHeight: 180,
          overflowY: 'auto',
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 9,
            color: '#8b949e',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 4,
          }}
        >
          DOMAINS
        </div>
        {manifest.domains.map((d) => (
          <button
            key={d.id}
            onClick={() => setFilters((prev) => ({ ...prev, domain: prev.domain === d.id ? '' : d.id }))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 0',
              color: filters.domain === d.id ? '#e6edf3' : '#8b949e',
              fontWeight: filters.domain === d.id ? 600 : 400,
              fontSize: 11,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: `var(${d.color})`,
                flexShrink: 0,
              }}
            />
            {d.id}
          </button>
        ))}
      </div>
    </div>
  );
}
